import { Box, Chip, LinearProgress, Typography } from '@mui/material';
import { IBudget } from 'common/entities';
import { sumWithCurrency } from 'core/services/currency.service';
import { ReactNode, useMemo } from 'react';

export interface AccumulationStatsProps {
  accumulation: IBudget;
}

const BudgetChip = ({ label }: { label: string }) => {
  return (
    <Chip
      sx={{ height: '1.2rem', fontSize: '0.5rem', fontWeight: 600 }}
      label={label}
      color="primary"
      size="small"
      variant="outlined"
    />
  );
};

const StatRow = ({
  name,
  value
}: {
  name: string;
  value?: string | ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      py: 0.5,
      alignItems: 'center'
    }}
  >
    <Typography>{name}</Typography>
    {value ? (
      <Typography sx={{ fontWeight: 500 }}>{value}</Typography>
    ) : (
      <Typography
        sx={{ fontSize: '0.8em', color: '#999' }}
      >{`<not set>`}</Typography>
    )}
  </Box>
);

const AccumulationStats = ({ accumulation }: AccumulationStatsProps) => {
  const accumulated = useMemo(
    () =>
      accumulation.periods.reduce(
        (sum, currentPeriod) => (sum += currentPeriod.budget ?? 0),
        0
      ),
    [accumulation]
  );

  const progress = useMemo(
    () =>
      accumulation.accumulationGoal
        ? (accumulated / accumulation.accumulationGoal) * 100
        : undefined,
    [accumulation, accumulated]
  );

  return (
    <Box sx={{ mb: 2 }}>
      <StatRow
        name="Status:"
        value={<BudgetChip label={accumulation.closed ? 'Closed' : 'Open'} />}
      />

      <StatRow
        name="Accumulation goal:"
        value={
          accumulation.accumulationGoal
            ? sumWithCurrency(accumulation.accumulationGoal)
            : undefined
        }
      />
      <StatRow name="Accumulated:" value={sumWithCurrency(accumulated)} />
      {accumulation.accumulationGoal && (
        <>
          <StatRow
            name="Remaining:"
            value={sumWithCurrency(accumulation.accumulationGoal - accumulated)}
          />
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: '1.2rem', width: '100%', mt: 1 }}
          />
        </>
      )}
    </Box>
  );
};

export default AccumulationStats;
